// ─── Collected Moments — by-person view ──────────────────────────
// Editorial dashboard surface that flips from "wishes/photos/voices" tabs into
// "Sofia: ✓note ·voice·photo / Bruno: ·note ✓voice ·photo" — how a wedding planner
// actually thinks. Lets the organizer add content on behalf of guests they collected
// from offline (in person, WhatsApp, dictation) so the morning reveal is honest
// and full even when guests didn't engage with the public link.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Heart, Image as ImageIcon, Mic, Plus, Search, Check, X, Upload, Square } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CollectedMomentsView({ eventId, token, adminDarkMode, onTotalsChange, autoOpenAdd, onAutoOpenConsumed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // search
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'with' | 'without'
  const [addOpen, setAddOpen] = useState(false);
  const [addForGuest, setAddForGuest] = useState(null); // {guest_name, guest_id?} when launching from a row

  const fetchCollected = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/events/${eventId}/collected`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      onTotalsChange?.(res.data?.summary || null);
    } catch { /* */ }
    setLoading(false);
  }, [eventId, token, onTotalsChange]);

  useEffect(() => { fetchCollected(); }, [fetchCollected]);

  // Auto-open the add modal when triggered from another tab (e.g. Reveal landing card)
  useEffect(() => {
    if (autoOpenAdd && !addOpen) {
      setAddForGuest(null);
      setAddOpen(true);
      onAutoOpenConsumed?.();
    }
  }, [autoOpenAdd, addOpen, onAutoOpenConsumed]);

  const c = {
    text: adminDarkMode ? 'white' : '#1e293b',
    muted: adminDarkMode ? '#94a3b8' : '#64748b',
    faint: adminDarkMode ? '#64748b' : '#94a3b8',
    border: adminDarkMode ? '#1e293b' : '#e2e8f0',
    cardBg: adminDarkMode ? '#0f172a' : 'white',
    rowHover: adminDarkMode ? '#1e293b' : '#f1f5f9',
    accent: '#7c3aed',
  };

  if (loading) {
    return <div className="text-sm" style={{ color: c.muted }}>Loading collected moments…</div>;
  }
  if (!data) {
    return <div className="text-sm" style={{ color: c.muted }}>Couldn’t load yet — try again.</div>;
  }

  const people = data.people || [];
  const filtered = people.filter(p => {
    const matches = !filter || (p.guest_name || '').toLowerCase().includes(filter.toLowerCase());
    if (!matches) return false;
    const has = p.wishes.length > 0 || p.photos.length > 0 || !!p.voice;
    if (activeTab === 'with') return has;
    if (activeTab === 'without') return !has;
    return true;
  });

  return (
    <div data-testid="collected-moments-view">
      {/* Header / counters */}
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-medium" style={{ color: c.text }}>Collected moments</h2>
          <p className="text-[13px] mt-1" style={{ color: c.muted }}>
            Still time to add a few more moments — share the link, or add what they gave you in person.
          </p>
        </div>
        <button
          onClick={() => { setAddForGuest(null); setAddOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
          style={{ backgroundColor: c.accent, color: 'white' }}
          data-testid="collected-add-for-guest-btn"
        >
          <Plus className="h-4 w-4" />
          Add for a guest
        </button>
      </div>

      {/* Soft summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Notes" value={data.summary.wishes_total} icon={null} c={c} />
        <SummaryCard label="Photos" value={data.summary.photos_total} c={c} />
        <SummaryCard label="Voices" value={data.summary.voices_total} c={c} />
        <SummaryCard label="Guests with moments" value={`${data.summary.with_any} / ${data.summary.guests_total}`} c={c} />
      </div>

      {/* Search + sub-tabs */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: c.faint }} />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search guests"
            className="w-full pl-9 pr-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-1"
            style={{ backgroundColor: c.cardBg, color: c.text, borderColor: c.border, boxShadow: 'none' }}
            data-testid="collected-search"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg p-1 border" style={{ borderColor: c.border, backgroundColor: c.cardBg }}>
          {[
            ['all', 'All'],
            ['with', 'With moments'],
            ['without', 'Still to gather'],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setActiveTab(v)}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
              style={{
                backgroundColor: activeTab === v ? c.accent : 'transparent',
                color: activeTab === v ? 'white' : c.muted,
              }}
              data-testid={`collected-filter-${v}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* People list */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: c.border, backgroundColor: c.cardBg }}>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm" style={{ color: c.muted }}>
            {filter ? 'No matches.' : 'No one yet.'}
          </div>
        ) : filtered.map((p, i) => (
          <PersonRow
            key={`${p.guest_id || p.guest_name}-${i}`}
            person={p}
            c={c}
            onAddForThem={() => { setAddForGuest({ guest_name: p.guest_name, guest_id: p.guest_id }); setAddOpen(true); }}
          />
        ))}
      </div>

      {/* Add for a guest modal */}
      {addOpen && (
        <AddForGuestModal
          eventId={eventId}
          token={token}
          adminDarkMode={adminDarkMode}
          guests={people.filter(p => p.is_invited).map(p => ({ guest_id: p.guest_id, guest_name: p.guest_name }))}
          prefilledGuest={addForGuest}
          onClose={() => { setAddOpen(false); setAddForGuest(null); }}
          onSaved={async () => { setAddOpen(false); setAddForGuest(null); await fetchCollected(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, c }) {
  return (
    <div
      className="rounded-lg p-3 border"
      style={{ borderColor: c.border, backgroundColor: c.cardBg }}
    >
      <p className="text-[11px] uppercase tracking-wide" style={{ color: c.faint }}>{label}</p>
      <p className="text-xl font-medium mt-1" style={{ color: c.text, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
    </div>
  );
}

function PersonRow({ person, c, onAddForThem }) {
  const hasNote = person.wishes.length > 0;
  const hasPhoto = person.photos.length > 0;
  const hasVoice = !!person.voice;
  const anyProxy =
    person.wishes.some(w => w.submitted_via === 'organizer_proxy') ||
    person.photos.some(p => p.submitted_via === 'organizer_proxy') ||
    (person.voice?.submitted_via === 'organizer_proxy');

  return (
    <div
      className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 transition-colors"
      style={{ borderColor: c.border }}
      data-testid={`collected-person-row-${person.guest_id || person.guest_name}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate" style={{ color: c.text }}>{person.guest_name}</p>
          {!person.is_invited && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: c.faint, border: `1px solid ${c.border}` }}>
              not on guest list
            </span>
          )}
          {anyProxy && (
            <span className="text-[10px]" style={{ color: c.faint }}>· some added by you</span>
          )}
        </div>
        {person.table_name && (
          <p className="text-[11px] mt-0.5" style={{ color: c.faint }}>{person.table_name}</p>
        )}
      </div>

      {/* Three slot indicators */}
      <div className="flex items-center gap-2">
        <SlotChip on={hasNote} count={person.wishes.length} c={c} icon="note" />
        <SlotChip on={hasVoice} count={hasVoice ? 1 : 0} c={c} icon="voice" />
        <SlotChip on={hasPhoto} count={person.photos.length} c={c} icon="photo" />
      </div>

      <button
        onClick={onAddForThem}
        className="text-xs font-medium transition-opacity hover:opacity-75"
        style={{ color: c.accent }}
        data-testid={`collected-add-for-${person.guest_id || person.guest_name}`}
      >
        Add for them &rarr;
      </button>
    </div>
  );
}

function SlotChip({ on, count, c, icon }) {
  const Icon = icon === 'note' ? NoteIconSvg : icon === 'voice' ? Mic : ImageIcon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px]"
      style={{
        color: on ? c.text : c.faint,
        backgroundColor: on ? (c.cardBg === 'white' ? '#f1f5f9' : '#1e293b') : 'transparent',
        border: `1px solid ${on ? c.border : 'transparent'}`,
        opacity: on ? 1 : 0.55,
      }}
    >
      <Icon className="h-3 w-3" />
      {on && count > 1 && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
    </span>
  );
}

function NoteIconSvg(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...props}>
      <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-5l-4 3v-3H6a2 2 0 0 1-2-2V6z" />
    </svg>
  );
}

// ─── Add for a Guest — modal ─────────────────────────────────────
function AddForGuestModal({ eventId, token, adminDarkMode, guests, prefilledGuest, onClose, onSaved }) {
  // Step state machine: 'who' → 'what' → 'note'|'photo'|'voice' → submit
  const [step, setStep] = useState(prefilledGuest ? 'what' : 'who');
  const [guestName, setGuestName] = useState(prefilledGuest?.guest_name || '');
  const [guestId, setGuestId] = useState(prefilledGuest?.guest_id || null);
  const [contentType, setContentType] = useState(null); // 'note' | 'photo' | 'voice'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Per-type form state
  const [message, setMessage] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoNote, setPhotoNote] = useState('');
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const [voiceMode, setVoiceMode] = useState('record'); // 'record' | 'upload'
  const [recState, setRecState] = useState('idle'); // idle | recording | done
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const autoStopRef = useRef(null);

  // Filter guests by name as you type
  const showSuggestions = step === 'who' && guestName.length > 0 && !guestId;
  const matches = showSuggestions
    ? guests.filter(g => g.guest_name.toLowerCase().includes(guestName.toLowerCase())).slice(0, 6)
    : [];

  const c = {
    text: adminDarkMode ? 'white' : '#0f172a',
    muted: adminDarkMode ? '#94a3b8' : '#64748b',
    faint: adminDarkMode ? '#64748b' : '#94a3b8',
    border: adminDarkMode ? '#1e293b' : '#e2e8f0',
    cardBg: adminDarkMode ? '#0f172a' : 'white',
    inputBg: adminDarkMode ? '#020617' : '#f8fafc',
    accent: '#7c3aed',
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { setError('Recording is not supported on this device.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const elapsed = Math.round((Date.now() - recordStartRef.current) / 1000);
        setVoiceBlob(blob);
        setVoiceDuration(elapsed);
        setRecState('done');
        try { stream.getTracks().forEach(t => t.stop()); } catch { /* */ }
      };
      mediaRecorderRef.current = mr;
      recordStartRef.current = Date.now();
      mr.start();
      setRecState('recording');
      autoStopRef.current = setTimeout(() => { try { mr.stop(); } catch { /* */ } }, 60000);
    } catch {
      setError('Microphone access was blocked.');
    }
  };

  const stopRecording = () => {
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    try { mediaRecorderRef.current?.stop(); } catch { /* */ }
  };

  const handleVoiceUploadFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setVoiceBlob(f);
    setVoiceDuration(0); // unknown — server can store 0
    setRecState('done');
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    const trimmedName = guestName.trim();
    if (!trimmedName) { setError('Please enter a guest name.'); setSubmitting(false); return; }
    try {
      if (contentType === 'note') {
        if (!message.trim()) { setError('Please add what they shared.'); setSubmitting(false); return; }
        await axios.post(`${API}/api/events/${eventId}/proxy/wish`, {
          guest_name: trimmedName,
          guest_id: guestId,
          message: message.trim(),
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else if (contentType === 'photo') {
        if (!photoFile) { setError('Please pick a photo.'); setSubmitting(false); return; }
        const fd = new FormData();
        fd.append('photo', photoFile);
        fd.append('guest_name', trimmedName);
        if (guestId) fd.append('guest_id', guestId);
        fd.append('note', photoNote || '');
        await axios.post(`${API}/api/events/${eventId}/proxy/photo`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      } else if (contentType === 'voice') {
        if (!voiceBlob) { setError('Please record or upload audio first.'); setSubmitting(false); return; }
        const fd = new FormData();
        fd.append('audio', voiceBlob, voiceBlob.name || 'voice.webm');
        fd.append('guest_name', trimmedName);
        if (guestId) fd.append('guest_id', guestId);
        fd.append('duration_sec', String(voiceDuration || 0));
        const res = await axios.post(`${API}/api/events/${eventId}/proxy/voice`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.already_exists) {
          setError(`${trimmedName} already has a voice message — only one per guest.`);
          setSubmitting(false);
          return;
        }
      }
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="add-for-guest-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl"
        style={{ backgroundColor: c.cardBg, border: `1px solid ${c.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h3 className="text-base font-medium" style={{ color: c.text }}>
            {step === 'who' && 'Who is this from?'}
            {step === 'what' && 'What did they share with you?'}
            {step === 'note' && `From ${guestName}`}
            {step === 'photo' && `From ${guestName}`}
            {step === 'voice' && `From ${guestName}`}
          </h3>
          <button onClick={onClose} className="opacity-50 hover:opacity-90 transition-opacity" data-testid="add-for-guest-close">
            <X className="h-4 w-4" style={{ color: c.muted }} />
          </button>
        </div>
        <p className="text-[12px] px-6 pb-4 italic" style={{ color: c.faint }}>
          {step === 'who' && 'Pick from your guest list, or type a name.'}
          {step === 'what' && 'Add only what was given to you, in person or privately.'}
          {step === 'note' && 'Type what they shared with you, in their words.'}
          {step === 'photo' && 'Upload a photo they sent you.'}
          {step === 'voice' && 'Record them speaking, or upload audio they sent.'}
        </p>

        <div className="px-6 pb-6">
          {/* STEP: WHO */}
          {step === 'who' && (
            <div className="relative">
              <input
                type="text"
                autoFocus
                value={guestName}
                onChange={(e) => { setGuestName(e.target.value); setGuestId(null); }}
                placeholder="Guest name"
                className="w-full px-3 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-1"
                style={{ backgroundColor: c.inputBg, color: c.text, borderColor: c.border }}
                data-testid="add-for-guest-name-input"
              />
              {showSuggestions && matches.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 rounded-lg border shadow-md z-10" style={{ backgroundColor: c.cardBg, borderColor: c.border }}>
                  {matches.map((g) => (
                    <button
                      key={g.guest_id || g.guest_name}
                      onClick={() => { setGuestName(g.guest_name); setGuestId(g.guest_id); }}
                      className="w-full text-left px-3 py-2 text-sm hover:opacity-80 first:rounded-t-lg last:rounded-b-lg"
                      style={{ color: c.text }}
                      data-testid={`add-for-guest-suggest-${g.guest_id}`}
                    >
                      {g.guest_name}
                    </button>
                  ))}
                </div>
              )}
              {showSuggestions && matches.length === 0 && (
                <p className="text-[11px] mt-2 italic" style={{ color: c.faint }}>
                  Not on the guest list — that&rsquo;s fine, we&rsquo;ll add them as a contributor.
                </p>
              )}
              <div className="mt-5 flex justify-end">
                <button
                  onClick={() => guestName.trim() && setStep('what')}
                  disabled={!guestName.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-30"
                  style={{ backgroundColor: c.accent, color: 'white' }}
                  data-testid="add-for-guest-next"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP: WHAT */}
          {step === 'what' && (
            <div className="space-y-2">
              <ContentTypeButton label="Text message" desc="Words they shared" onClick={() => { setContentType('note'); setStep('note'); }} c={c} testid="ctype-note" />
              <ContentTypeButton label="Photo" desc="An image they sent you" onClick={() => { setContentType('photo'); setStep('photo'); }} c={c} testid="ctype-photo" />
              <ContentTypeButton label="Voice message" desc="Record them or upload audio" onClick={() => { setContentType('voice'); setStep('voice'); }} c={c} testid="ctype-voice" />
              <button onClick={() => setStep('who')} className="text-xs mt-3" style={{ color: c.muted }}>&larr; Back</button>
            </div>
          )}

          {/* STEP: NOTE */}
          {step === 'note' && (
            <div className="space-y-3">
              <textarea
                autoFocus
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="What did they say?"
                className="w-full px-3 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-1 resize-none"
                style={{ backgroundColor: c.inputBg, color: c.text, borderColor: c.border }}
                data-testid="proxy-note-textarea"
              />
              <ActionRow onBack={() => setStep('what')} onSubmit={handleSubmit} canSubmit={!!message.trim()} submitting={submitting} c={c} />
            </div>
          )}

          {/* STEP: PHOTO */}
          {step === 'photo' && (
            <div className="space-y-3">
              {photoPreview ? (
                <div className="relative">
                  <img src={photoPreview} alt="" className="w-full h-48 object-cover rounded-lg" />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"
                  ><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer transition-colors hover:opacity-80"
                       style={{ borderColor: c.border, color: c.muted }}>
                  <Upload className="h-5 w-5 mb-1.5" />
                  <span className="text-sm">Choose photo</span>
                  <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="proxy-photo-file-input" />
                </label>
              )}
              <input
                type="text"
                value={photoNote}
                onChange={(e) => setPhotoNote(e.target.value)}
                placeholder="Optional caption"
                className="w-full px-3 py-2 rounded-lg text-sm border"
                style={{ backgroundColor: c.inputBg, color: c.text, borderColor: c.border }}
                data-testid="proxy-photo-note"
              />
              <ActionRow onBack={() => setStep('what')} onSubmit={handleSubmit} canSubmit={!!photoFile} submitting={submitting} c={c} />
            </div>
          )}

          {/* STEP: VOICE */}
          {step === 'voice' && (
            <div className="space-y-3">
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => { setVoiceMode('record'); setVoiceBlob(null); setRecState('idle'); }}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium border"
                  style={{
                    backgroundColor: voiceMode === 'record' ? c.accent : 'transparent',
                    color: voiceMode === 'record' ? 'white' : c.muted,
                    borderColor: c.border,
                  }}
                  data-testid="proxy-voice-tab-record"
                >Record</button>
                <button
                  onClick={() => { setVoiceMode('upload'); setVoiceBlob(null); setRecState('idle'); }}
                  className="flex-1 px-3 py-2 rounded-lg text-xs font-medium border"
                  style={{
                    backgroundColor: voiceMode === 'upload' ? c.accent : 'transparent',
                    color: voiceMode === 'upload' ? 'white' : c.muted,
                    borderColor: c.border,
                  }}
                  data-testid="proxy-voice-tab-upload"
                >Upload audio</button>
              </div>

              {voiceMode === 'record' ? (
                <div className="rounded-lg p-6 flex flex-col items-center justify-center" style={{ backgroundColor: c.inputBg }}>
                  {recState === 'idle' && (
                    <button
                      onClick={startRecording}
                      className="flex flex-col items-center gap-2"
                      data-testid="proxy-voice-record-start"
                    >
                      <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: c.accent, color: 'white' }}>
                        <Mic className="h-5 w-5" />
                      </span>
                      <span className="text-xs" style={{ color: c.muted }}>Tap to record (up to 60s)</span>
                    </button>
                  )}
                  {recState === 'recording' && (
                    <button
                      onClick={stopRecording}
                      className="flex flex-col items-center gap-2"
                      data-testid="proxy-voice-record-stop"
                    >
                      <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: '#ef4444', animation: 'recPulse 1.4s ease-in-out infinite' }}>
                        <Square className="h-5 w-5" fill="white" />
                      </span>
                      <span className="text-xs" style={{ color: c.muted }}>Tap to stop</span>
                      <style>{`@keyframes recPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }`}</style>
                    </button>
                  )}
                  {recState === 'done' && voiceBlob && (
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-sm" style={{ color: c.text }}>
                        <Check className="inline h-4 w-4 mr-1" style={{ color: '#22c55e' }} />
                        Recorded ({voiceDuration}s)
                      </p>
                      <button
                        onClick={() => { setVoiceBlob(null); setRecState('idle'); setVoiceDuration(0); }}
                        className="text-xs underline-offset-4 hover:underline"
                        style={{ color: c.muted }}
                      >Re-record</button>
                    </div>
                  )}
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors hover:opacity-80"
                       style={{ borderColor: c.border, color: c.muted }}>
                  {voiceBlob ? (
                    <>
                      <Check className="h-5 w-5 mb-1" style={{ color: '#22c55e' }} />
                      <span className="text-sm" style={{ color: c.text }}>{voiceBlob.name || 'Audio ready'}</span>
                      <span className="text-[11px] mt-0.5" style={{ color: c.faint }}>tap to choose another</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-5 w-5 mb-1.5" />
                      <span className="text-sm">Choose audio file</span>
                    </>
                  )}
                  <input type="file" accept="audio/*" onChange={handleVoiceUploadFile} className="hidden" data-testid="proxy-voice-upload-file" />
                </label>
              )}
              <ActionRow onBack={() => setStep('what')} onSubmit={handleSubmit} canSubmit={!!voiceBlob} submitting={submitting} c={c} />
            </div>
          )}

          {error && <p className="text-xs mt-3" style={{ color: '#ef4444' }} data-testid="add-for-guest-error">{error}</p>}
        </div>
      </div>
    </div>
  );
}

function ContentTypeButton({ label, desc, onClick, c, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 rounded-lg border transition-all hover:opacity-80"
      style={{ borderColor: c.border, backgroundColor: c.inputBg }}
      data-testid={testid}
    >
      <p className="text-sm font-medium" style={{ color: c.text }}>{label}</p>
      <p className="text-[11px] mt-0.5" style={{ color: c.faint }}>{desc}</p>
    </button>
  );
}

function ActionRow({ onBack, onSubmit, canSubmit, submitting, c }) {
  return (
    <div className="flex items-center justify-between mt-4">
      <button onClick={onBack} className="text-xs" style={{ color: c.muted }}>&larr; Back</button>
      <button
        onClick={onSubmit}
        disabled={!canSubmit || submitting}
        className="px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-30"
        style={{ backgroundColor: c.accent, color: 'white' }}
        data-testid="add-for-guest-submit"
      >
        {submitting ? 'Adding…' : 'Add'}
      </button>
    </div>
  );
}
